/**
 * Cata-Kor quiz — weighted answer scoring (same pattern as ProDenta: per-option
 * weights, capped multi-select sums, normalize by theoretical max, four tiers).
 * Higher raw / normalized = worse cellular / NAD outlook.
 */
(function (global) {
  "use strict";

  /** Sum of per-step maximum contributions — tune when weights change. */
  var THEORETICAL_MAX = 13.75;

  var QUARTERS = [0.24, 0.48, 0.72];

  /**
   * Single-select and slider weights. Multi-select steps handled separately.
   */
  var SINGLE_WEIGHTS = {
    q1: {
      energy: 0.14,
      focus: 0.12,
      antiaging: 0.10,
      sleep: 0.16,
      skin: 0.12,
    },
    q2: {
      exhausted: 1.0,
      tired: 0.68,
      varies: 0.32,
      rested: 0,
    },
    q3: {
      essential: 0.88,
      high: 0.52,
      low: 0.18,
      none: 0,
    },
    q4: {
      yes: 0.78,
      no: 0,
    },
    q6: {
      avoid: 0,
      rarely: 0.16,
      sometimes: 0.42,
      most_days: 0.78,
    },
    q9: {
      yes: 0.48,
      no: 0,
    },
    q11: {
      regular: 0,
      sometimes: 0.22,
      rarely: 0.48,
    },
    q13: {
      rarely: 0,
      weekends: 0.26,
      few_per_week: 0.48,
      daily: 0.88,
    },
    q14: {
      yes: 0.28,
      no: 0,
    },
    q15: {
      perimenopause: 0.36,
      menopause: 0.42,
      noticing: 0.26,
      unsure: 0.14,
      na: 0,
      declining_noticeably: 0.42,
      slowly_slipping: 0.26,
      still_solid: 0.08,
      not_sure: 0.16,
    },
    q16: {
      "65_plus": 1.05,
      "55_64": 0.78,
      "45_54": 0.52,
      "35_44": 0.32,
      "25_34": 0.16,
      "18_24": 0.05,
    },
    q18: {
      lt_1y: 0.16,
      "1_3y": 0.36,
      "3_5y": 0.56,
      gt_5y: 0.78,
      forever: 0.95,
    },
  };

  /** Multi-select: per-option weights; cap total; optional none-only behavior. */
  var MULTI_CONFIG = {
    q5: {
      cap: 1.45,
      noneOnlyContribution: 0,
      noneValues: ["nothing"],
      weights: {
        caffeine: 0.28,
        multivitamins: 0.16,
        diet: 0.14,
        exercise: 0.1,
        supplements: 0.22,
        iv: 0.38,
        nothing: 0,
      },
    },
    q7: {
      cap: 1.58,
      noneOnlyContribution: 0,
      noneValues: ["none"],
      weights: {
        energy_crash: 0.22,
        brain_fog: 0.22,
        couch: 0.18,
        tired: 0.2,
        skin_dull: 0.14,
        recovery: 0.18,
        sick: 0.2,
        self: 0.2,
        none: 0,
      },
    },
    q8: {
      cap: 0.98,
      noneOnlyContribution: 0,
      noneValues: ["none"],
      weights: {
        low_energy: 0.32,
        cravings: 0.24,
        stress: 0.28,
        cognitive: 0.32,
        none: 0,
      },
    },
    q12: {
      cap: 0,
      noneOnlyContribution: 0.45,
      noneValues: ["none"],
      weights: {
        cardio: 0,
        strength: 0,
        yoga: 0,
        walking: 0,
        sports: 0,
        none: 0,
      },
    },
  };

  var MULTI_IDS = Object.keys(MULTI_CONFIG);
  var SINGLE_IDS = Object.keys(SINGLE_WEIGHTS).slice();

  function round2(x) {
    return Math.round(x * 100) / 100;
  }

  function multiStepContribution(stepId, selected) {
    var cfg = MULTI_CONFIG[stepId];
    if (!cfg) return 0;
    var arr = Array.isArray(selected) ? selected : [];
    var noneSet = cfg.noneValues || [];
    var substantive = arr.filter(function (v) {
      return noneSet.indexOf(v) < 0;
    });
    var onlyNone =
      arr.length > 0 &&
      substantive.length === 0 &&
      noneSet.length > 0;
    if (onlyNone) {
      return cfg.noneOnlyContribution == null ? 0 : cfg.noneOnlyContribution;
    }
    var sum = 0;
    substantive.forEach(function (v) {
      sum += (cfg.weights[v] !== undefined ? cfg.weights[v] : 0);
    });
    if (cfg.cap > 0) return Math.min(round2(sum), cfg.cap);
    return round2(sum);
  }

  function sliderContribution(answerStr) {
    var n = parseInt(answerStr, 10);
    if (isNaN(n)) return round2(((11 - 5) / 10) * 1.15);
    n = Math.max(1, Math.min(10, n));
    return round2(((11 - n) / 10) * 1.15);
  }

  function singleContribution(stepId, value) {
    var map = SINGLE_WEIGHTS[stepId];
    if (!map || value == null || value === "") return 0;
    var w = map[value];
    return w !== undefined ? w : 0;
  }

  /**
   * @param {Record<string, *>} answers — catakor quiz answers by step id
   * @returns {{ raw: number, normalized: number, tier: string, tierIndex: number }}
   */
  function computeRisk(answers) {
    answers = answers || {};
    var total = 0;

    for (var i = 0; i < MULTI_IDS.length; i++) {
      total += multiStepContribution(MULTI_IDS[i], answers[MULTI_IDS[i]]);
    }
    for (var j = 0; j < SINGLE_IDS.length; j++) {
      total += singleContribution(SINGLE_IDS[j], answers[SINGLE_IDS[j]]);
    }
    total += sliderContribution(answers.q17 != null ? String(answers.q17) : "");

    total = round2(total);
    var normalized = Math.min(1, total / THEORETICAL_MAX);

    var tier;
    var tierIndex;
    if (normalized < QUARTERS[0]) {
      tier = "normal";
      tierIndex = 0;
    } else if (normalized < QUARTERS[1]) {
      tier = "moderate";
      tierIndex = 1;
    } else if (normalized < QUARTERS[2]) {
      tier = "high";
      tierIndex = 2;
    } else {
      tier = "critical";
      tierIndex = 3;
    }

    return {
      raw: total,
      normalized: normalized,
      tier: tier,
      tierIndex: tierIndex,
      theoreticalMax: THEORETICAL_MAX,
      indicatorPct: { normal: 14, moderate: 38, high: 62, critical: 88 }[tier],
    };
  }

  global.CataKorQuizScoring = {
    THEORETICAL_MAX: THEORETICAL_MAX,
    QUARTERS: QUARTERS.slice(),
    computeRisk: computeRisk,
    multiStepContribution: multiStepContribution,
    sliderContribution: sliderContribution,
  };
})(typeof window !== "undefined" ? window : globalThis);
